/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useRef } from 'react';
import { Box, FormLabel, InputAdornment, MenuItem, styled } from '@mui/material';
import DateRangePicker, { DateRangeType } from '../../../../components/DateRangePicker';
import { ValueAndSetValue, FromTo } from '../../../../components/Filters/types';
import Input from '../../../../components/Input';
import TestIds from '../../../../testIds';
import { unflattenCategories } from '../../../typesense/utils';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { colors } from '@farmlands/mui-theme';
import Typography from '../../../../components/Typography';
import Checkbox from '../../../../components/Checkbox';

const FilterWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: '12px',
  maxWidth: '336px',
  [theme.breakpoints.down('md')]: {
    maxWidth: 'unset',
  },
}));

export const StyledMenuItem = styled(MenuItem)({
  paddingLeft: 0,
  paddingRight: 0,
  '&:focus': {
    background: 'transparent',
  },
});

export interface DesktopDateRangeProps {
  label?: string;
  dateRange?: DateRangeType;
  handleDateRange: (dateRange: DateRangeType) => void;
}

export interface DesktopCategoryMenuProps {
  filters: { categories: string[] };
  selectedFilters: ValueAndSetValue<{
    category: string;
  }>;
  showSubCategories?: boolean;
}

interface DesktopPriceRangeProps {
  selectedFilters: ValueAndSetValue<{
    priceRange: FromTo<number>;
  }>;
  label?: string;
}

const isInputValid = (value: number) => !isNaN(value) && value >= 0 && value < 100000000;

export const DesktopPriceRangeFilter: React.FC<DesktopPriceRangeProps> = ({
  selectedFilters,
  label,
}) => {
  return (
    <FilterWrapper>
      {label && <FormLabel>{label}</FormLabel>}
      <Box display='inline-flex' gap={'11px'} alignItems='center'>
        <Input
          outlineInputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          disableError
          label=''
          setValue={(v) => {
            if (!v) {
              selectedFilters.priceRange.setValue({
                ...selectedFilters.priceRange.value,
                from: undefined,
              });
            } else if (v.valueOf()) {
              const inputValue = parseFloat(v.valueOf() as string);
              if (isInputValid(inputValue)) {
                selectedFilters.priceRange.setValue({
                  ...selectedFilters.priceRange.value,
                  from: inputValue,
                });
              }
            }
          }}
          placeholder='From'
          testId={TestIds.components.filters.fromPrice}
          type='number'
          value={selectedFilters.priceRange.value.from?.toString() ?? ''}
        />

        <Typography variant='body2' component='span' color='primary800'>
          -
        </Typography>
        <Input
          outlineInputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          disableError
          label=''
          placeholder='To'
          setValue={(v) => {
            if (!v) {
              selectedFilters.priceRange.setValue({
                ...selectedFilters.priceRange.value,
                to: undefined,
              });
            } else if (v.valueOf()) {
              const inputValue = parseFloat(v.valueOf() as string);
              if (isInputValid(inputValue)) {
                selectedFilters.priceRange.setValue({
                  ...selectedFilters.priceRange.value,
                  to: inputValue,
                });
              }
            }
          }}
          testId={TestIds.components.filters.toPrice}
          type='number'
          value={selectedFilters.priceRange.value.to?.toString() ?? ''}
        />
      </Box>
    </FilterWrapper>
  );
};
export const DesktopCategoryMenu: React.FC<DesktopCategoryMenuProps> = ({
  selectedFilters,
  filters,
  showSubCategories = false,
}) => {
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    selectedFilters.category.value?.split('>').at(0) ?? null,
  );
  const [selectedSubCategory, setSelectedSubCategory] = React.useState<string | null>(
    selectedFilters.category.value?.split('>').at(1) ?? null,
  );
  const unflattenedCategories = unflattenCategories(filters.categories);

  const ref = useRef<HTMLDivElement>();

  const handleCategoryClick = (category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }

    selectedFilters.category.setValue(category);
  };

  const handleSubCategoryClick = (category: string) => {
    if (selectedSubCategory === category) {
      setSelectedSubCategory(null);
    } else {
      setSelectedSubCategory(category);
    }
    if (selectedCategory) {
      selectedFilters.category.setValue(selectedCategory + '>' + category);
    }
  };

  return (
    <Box
      display='grid'
      gap='12px'
      gridTemplateColumns={showSubCategories ? '1fr 1fr' : '1fr'}
      ref={ref}
    >
      <Box maxHeight={300} overflow='scroll' className='scrollbox'>
        {Object.keys(unflattenedCategories).map((category: string) => {
          const selectedCategory = selectedFilters.category.value?.split('>').at(0) === category;

          return (
            <StyledMenuItem
              onClickCapture={() => handleCategoryClick(category)}
              key={category}
              data-testid={`${TestIds.components.filters.categoryDropdownMenuItem}`}
              data-id={`${TestIds.components.filters.categoryDropdownMenuItemDataId(category)}`}
            >
              <Box display='inline-flex'>
                <MaterialSymbol
                  sx={{
                    opacity: selectedCategory ? 1 : 0,
                    color: colors.grey800,
                  }}
                >
                  check
                </MaterialSymbol>
                <Typography
                  color={selectedCategory ? 'grey800' : 'primary500'}
                  fontWeight={selectedCategory ? 'bold' : 'inherit'}
                  variant='body2'
                  sx={{ ml: '0.5rem' }}
                >
                  {category}
                </Typography>
              </Box>
            </StyledMenuItem>
          );
        })}
      </Box>
      {selectedCategory && unflattenedCategories[selectedCategory] && (
        <Box maxHeight={300} overflow='scroll' className='scrollbox'>
          {unflattenedCategories[selectedCategory].map((subcategory: string) => {
            const selectedCategory =
              selectedFilters.category.value?.split('>').at(1) === subcategory;

            return (
              <StyledMenuItem
                onClickCapture={() => handleSubCategoryClick(subcategory)}
                key={subcategory}
                data-testid={`${TestIds.components.filters.categoryDropdownMenuItem}`}
                data-id={`${TestIds.components.filters.categoryDropdownMenuItemDataId(subcategory)}`}
              >
                <Box display='inline-flex'>
                  <MaterialSymbol
                    sx={{
                      opacity: selectedCategory ? 1 : 0,
                      color: colors.grey800,
                    }}
                  >
                    check
                  </MaterialSymbol>
                  <Typography
                    color={selectedCategory ? 'grey800' : 'primary500'}
                    fontWeight={selectedCategory ? 'bold' : 'inherit'}
                    variant='body2'
                    sx={{ ml: '0.5rem' }}
                  >
                    {subcategory}
                  </Typography>
                </Box>
              </StyledMenuItem>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
export const DesktopDateRangePicker: React.FC<DesktopDateRangeProps> = ({
  dateRange,
  handleDateRange,
  label,
}) => {
  return (
    <FilterWrapper>
      <DateRangePicker
        label={label}
        testId={TestIds.components.filters.dateSelector}
        dateRange={
          dateRange ?? {
            from: undefined,
            to: undefined,
          }
        }
        setDateRange={handleDateRange}
      />
    </FilterWrapper>
  );
};

export interface DesktopInStockFilterProps {
  selectedFilters?: ValueAndSetValue<{
    inStock: boolean;
  }>;
}

export const DesktopInStockFilter: React.FC<DesktopInStockFilterProps> = ({ selectedFilters }) => {
  return (
    <Checkbox
      label='In stock only'
      checked={selectedFilters?.inStock.value}
      onChange={(_, checked) => selectedFilters?.inStock.setValue(checked)}
      data-testid={TestIds.components.filters.inStockCheckbox}
      sx={{
        marginTop: -2,
        margin: '-6px 0px 0px 6px!important',
        '& label': {},
      }}
    />
  );
};
